import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Modal,
  Backdrop,
  Fade,
  Chip,
  IconButton,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';
import { PLAY_STORE_URL, APP_STORE_URL } from '../../../constants/links';
import { GooglePlayIcon, AppleIcon } from '../../common/StoreIcons';

// Icons
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import VideoCallRoundedIcon from '@mui/icons-material/VideoCallRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import TheaterComedyRoundedIcon from '@mui/icons-material/TheaterComedyRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import DynamicFeedRoundedIcon from '@mui/icons-material/DynamicFeedRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

// Video asset
import chatVedio from '../../../assets/vedio/chatVedio.mp4';

const categories = [
  'All Features',
  'Logistics & Pre-Prod',
  'Live & Connect',
  'Talent & Casting',
  'Market & Promo',
];

const features = [
  {
    id: 1,
    title: 'Shooting Location Directory',
    category: 'Logistics & Pre-Prod',
    badge: 'Logistics',
    description: 'Browse, book, and list verified shooting locations — from heritage estates and modern studios to scenic outdoor locations.',
    fullDetails: 'FilmHook Media’s Shooting Location directory is an essential hub where filmmakers, producers, and photographers discover and secure spaces suitable for filming. Property owners can list private homes, studios, and scenic spots, complete with technical specs, power availability, and permit assistance. Production teams can browse geo-tagged venues, schedule site inspections, and negotiate directly with owners, dramatically accelerating production logistics.',
    icon: <LocationOnRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #0891b2 0%, #0284c7 100%)',
    bg: 'rgba(6, 182, 212, 0.12)',
    border: 'rgba(6, 182, 212, 0.3)',
    highlights: ['Studio Booking', 'Scenic Venues', 'Scout Direct Chat'],
  },
  {
    id: 2,
    title: 'Live Video Broadcasting',
    category: 'Live & Connect',
    badge: 'Live Streaming',
    description: 'Engage audiences and industry followers with low-latency live video streaming, real-time chats, and interactive host features.',
    fullDetails: 'FilmHook Media’s Live Option enables creators, actors, and directors to broadcast real-time video streams with interactive chats and reactions. Whether hosting movie premiere discussions, masterclasses, behind-the-scenes set tours, or Q&A sessions with fans, this tool delivers crystal-clear streaming with audience moderation tools and professional host controls.',
    icon: <SensorsRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#f43f5e',
    gradient: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)',
    bg: 'rgba(244, 63, 94, 0.12)',
    border: 'rgba(244, 63, 94, 0.3)',
    highlights: ['Ultra-HD Streaming', 'Real-time Chat', 'Live Q&A Sessions'],
  },
  {
    id: 3,
    title: 'Encrypted Chat & Calls',
    category: 'Live & Connect',
    badge: 'Communication',
    description: 'End-to-end encrypted messaging, up to 2GB dailies file transfer, and crystal-clear voice/video calling built for film crews.',
    fullDetails: 'FilmHook Media’s Chat & Calls suite offers private, encrypted communication tailored specifically for production teams and creative talent. Transfer heavy production media, script revisions, and lookbooks up to 2GB directly in chats. Conduct group voice and video calls with crew members, label conversations with custom tags like "Casting" or "Post-Production", and schedule calendar shoot dates directly inside the thread.',
    icon: <VideoCallRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
    bg: 'rgba(99, 102, 241, 0.12)',
    border: 'rgba(99, 102, 241, 0.3)',
    highlights: ['2GB File Sharing', 'VoIP HD Calls', 'Casting Chat Tags'],
    video: chatVedio,
  },
  {
    id: 4,
    title: 'Stories & Behind-The-Scenes',
    category: 'Live & Connect',
    badge: 'Stories & Reels',
    description: 'Share 24-hour fleeting production moments, onset diaries, look tests, and trailer teasers with your creative network.',
    fullDetails: 'The Story feature lets creators share fleeting behind-the-scenes moments, camera tests, and quick daily highlights. Build excitement and anticipation for upcoming film releases, share candid onset experiences, and give collaborators an authentic look into your creative workflow in an interactive, visually engaging format.',
    icon: <AutoStoriesRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    bg: 'rgba(16, 185, 129, 0.12)',
    border: 'rgba(16, 185, 129, 0.3)',
    highlights: ['24h BTS Exclusives', 'Trailer Teasers', 'Crew Day Diaries'],
  },
  {
    id: 5,
    title: 'Proximity Talent Radar',
    category: 'Talent & Casting',
    badge: 'Talent Discovery',
    description: 'Discover accredited cinematographers, actors, voice artists, and crew members located near your filming location in real time.',
    fullDetails: 'FilmHook Media’s Near to Me radar connects filmmakers with local industry professionals. Search by exact discipline (e.g. Focus Puller, Gaffer, Makeup Artist, Lead Actor), verify credentials and portfolios, and assemble last-minute crew additions or local line production units with ease.',
    icon: <RadarRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
    bg: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.3)',
    highlights: ['Geo-Radius Search', 'Role Filtering', 'Verified Credentials'],
  },
  {
    id: 6,
    title: 'Cinema Gear Marketplace',
    category: 'Market & Promo',
    badge: 'Marketplace',
    description: 'Buy, sell, or rent cinema cameras, anamorphic lenses, lighting packages, sound kits, and production props safely.',
    fullDetails: 'A dedicated marketplace exclusively for media equipment, cinema rentals, and production assets. Connect directly with trusted equipment houses, independent camera operators, and prop masters. Secure escrow payments and verified user histories ensure reliable gear exchanges for your shoot.',
    icon: <StorefrontRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
    bg: 'rgba(139, 92, 246, 0.12)',
    border: 'rgba(139, 92, 246, 0.3)',
    highlights: ['Camera & Lens Deals', 'Lighting Rigs', 'Escrow Security'],
  },
  {
    id: 7,
    title: 'Targeted Promotion Tools',
    category: 'Market & Promo',
    badge: 'Promotion',
    description: 'Amplify films, trailers, casting calls, and creative agencies with targeted in-app promotional placements and analytics.',
    fullDetails: 'Designed for production banners, film publicists, and independent artists looking to maximize reach. Run strategic banner and feed promotions targeted to specific industry roles, regions, and genres. Monitor real-time impressions, click-through rates, and engagement data.',
    icon: <CampaignRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#0284c7',
    gradient: 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
    bg: 'rgba(2, 132, 199, 0.12)',
    border: 'rgba(2, 132, 199, 0.3)',
    highlights: ['Targeted Impressions', 'Real-Time Analytics', 'Banner Placements'],
  },
  {
    id: 8,
    title: 'Verified Casting & Auditions',
    category: 'Talent & Casting',
    badge: 'Casting Engine',
    description: 'Confidential, verified audition calls for feature films, commercials, and OTT series with direct showreel submissions.',
    fullDetails: 'Exclusively accessible to verified casting directors and accredited actors. Post role breakdowns, character sides, and audition requirements with discretion and privacy. Actors submit video auditions and digital portfolios directly, streamlining the entire shortlist and callback workflow.',
    icon: <TheaterComedyRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#e11d48',
    gradient: 'linear-gradient(135deg, #be123c 0%, #f43f5e 100%)',
    bg: 'rgba(225, 29, 72, 0.12)',
    border: 'rgba(225, 29, 72, 0.3)',
    highlights: ['NDA Role Breakdowns', 'Video Audition Tapes', 'Casting Callbacks'],
  },
  {
    id: 9,
    title: 'Industry Professional Profile',
    category: 'Talent & Casting',
    badge: 'Digital Resume',
    description: 'Digital film resume showcasing filmography, verified credits, biometric measurements, agency representation, and awards.',
    fullDetails: 'Present a comprehensive, industry-standard digital portfolio. Feature your verified filmography, headshots, showreels, physical measurements for costume fitting, union affiliations, and verified agent contact details in a single elegant profile.',
    icon: <BadgeRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#9333ea',
    gradient: 'linear-gradient(135deg, #7e22ce 0%, #a855f7 100%)',
    bg: 'rgba(147, 51, 234, 0.12)',
    border: 'rgba(147, 51, 234, 0.3)',
    highlights: ['Verified Filmography', 'Costume Measurements', 'Agency Badges'],
  },
  {
    id: 10,
    title: 'Instant Project Notifications',
    category: 'Live & Connect',
    badge: 'Alerts',
    description: 'Never miss an audition callback, script revision, or casting ping with customized real-time push notifications.',
    fullDetails: 'Keep up with critical production timelines. Receive instant push notifications whenever a director reviews your audition tape, a collaborator comments on your call sheet, or a new shooting location matching your project specs becomes available.',
    icon: <NotificationsActiveRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#eab308',
    gradient: 'linear-gradient(135deg, #ca8a04 0%, #eab308 100%)',
    bg: 'rgba(234, 179, 8, 0.12)',
    border: 'rgba(234, 179, 8, 0.3)',
    highlights: ['Role Callback Alerts', 'Script Revision Push', 'Collab Notifications'],
  },
  {
    id: 11,
    title: 'Cinema Community Feeds',
    category: 'Live & Connect',
    badge: 'Media Feed',
    description: 'Engage with fellow industry creators, share high-res showreels, discuss production gear, and build your professional network.',
    fullDetails: 'Share production stills, cinematography breakdowns, color grading lookbooks, and tech discussions with a passionate cinema community. Exchange constructive critique, discover upcoming indie productions, and build valuable partnerships.',
    icon: <DynamicFeedRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#0d9488',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    bg: 'rgba(13, 148, 136, 0.12)',
    border: 'rgba(13, 148, 136, 0.3)',
    highlights: ['4K Showreel Feeds', 'Color Grade Breakdowns', 'Discussion Threads'],
  },
  {
    id: 12,
    title: 'Accredited Industry Reviews',
    category: 'Market & Promo',
    badge: 'Reputation',
    description: 'Verified peer ratings and testimonials based exclusively on completed film productions and documented contracts.',
    fullDetails: 'FilmHook Media eliminates fraudulent ratings by restricting reviews strictly to verified collaborators on documented, completed projects. Directors, cinematographers, and actors receive accredited endorsements that substantiate their reliability and on-set professionalism.',
    icon: <VerifiedRoundedIcon sx={{ fontSize: 28 }} />,
    color: '#2563eb',
    gradient: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
    bg: 'rgba(37, 99, 235, 0.12)',
    border: 'rgba(37, 99, 235, 0.3)',
    highlights: ['Project-Linked Reviews', 'Accredited Badges', 'Fraud-Proof Reputation'],
  },
];

const FeaturesGridSection = () => {
  const { isDarkMode } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('All Features');
  const [openIndex, setOpenIndex] = useState(null);

  const filteredFeatures =
    selectedCategory === 'All Features'
      ? features
      : features.filter((f) => f.category === selectedCategory);

  const handleOpen = (index) => setOpenIndex(index);
  const handleClose = () => setOpenIndex(null);

  return (
    <Box
      id="filmhook-apps-features"
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 3, md: 5 },
        backgroundColor: isDarkMode ? '#060a16' : '#f8fafc',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background ambient lighting */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '70%',
          height: '400px',
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(7, 164, 245, 0.08) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(7, 164, 245, 0.05) 0%, transparent 70%)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ maxWidth: '1360px', mx: 'auto', position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2.2,
              py: 0.8,
              borderRadius: '30px',
              background: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : 'rgba(7, 164, 245, 0.08)',
              border: isDarkMode ? '1px solid rgba(7, 164, 245, 0.25)' : '1px solid rgba(7, 164, 245, 0.2)',
              mb: 2.5,
            }}
          >
            <AutoAwesomeRoundedIcon sx={{ color: '#07a4f5', fontSize: '1.1rem' }} />
            <Typography
              sx={{
                color: '#07a4f5',
                fontSize: '0.85rem',
                fontWeight: 800,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Filmhook Apps Capabilities
            </Typography>
          </Box>

          <Typography
            variant="h2"
            fontWeight={900}
            sx={{
              color: isDarkMode ? '#ffffff' : '#0d1530',
              mb: 2,
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.4rem' },
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
            }}
          >
            Powerful Features Built for{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(90deg, #07a4f5 0%, #3b82f6 50%, #8b5cf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Modern Creators
            </Box>
          </Typography>

          <Typography
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              fontSize: { xs: '0.98rem', md: '1.12rem' },
              maxWidth: '680px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            From scouting verified shooting locations to confidential casting calls and encrypted crew messaging, explore the full creative toolkit inside Filmhook Apps.
          </Typography>

          {/* Dynamic Filter Pills */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 1.2,
              mt: 4,
            }}
          >
            {categories.map((cat) => {
              const isSelected = selectedCategory === cat;
              return (
                <Chip
                  key={cat}
                  label={cat}
                  onClick={() => setSelectedCategory(cat)}
                  sx={{
                    px: 1,
                    py: 2.2,
                    borderRadius: '24px',
                    fontWeight: 700,
                    fontSize: '0.86rem',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    bgcolor: isSelected
                      ? '#07a4f5'
                      : isDarkMode
                      ? 'rgba(255,255,255,0.05)'
                      : '#ffffff',
                    color: isSelected
                      ? '#ffffff'
                      : isDarkMode
                      ? '#cbd5e1'
                      : '#475569',
                    border: isSelected
                      ? '1px solid #07a4f5'
                      : isDarkMode
                      ? '1px solid rgba(255,255,255,0.08)'
                      : '1px solid #e2e8f0',
                    boxShadow: isSelected
                      ? '0 6px 20px rgba(7, 164, 245, 0.35)'
                      : isDarkMode
                      ? 'none'
                      : '0 2px 8px rgba(0,0,0,0.03)',
                    '&:hover': {
                      bgcolor: isSelected
                        ? '#068bc2'
                        : isDarkMode
                        ? 'rgba(255,255,255,0.1)'
                        : '#f1f5f9',
                      transform: 'translateY(-1px)',
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Feature Cards Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          <AnimatePresence>
            {filteredFeatures.map((feature, index) => {
              const globalIndex = features.findIndex((f) => f.id === feature.id);

              return (
                <motion.div
                  key={feature.id}
                  layout
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.35, delay: index * 0.04 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '24px',
                      backgroundColor: isDarkMode ? '#0d1424' : '#ffffff',
                      border: isDarkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(226, 232, 240, 0.9)',
                      boxShadow: isDarkMode
                        ? '0 10px 30px rgba(0, 0, 0, 0.3)'
                        : '0 10px 30px rgba(15, 23, 42, 0.04)',
                      overflow: 'hidden',
                      transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-7px)',
                        borderColor: feature.color,
                        boxShadow: isDarkMode
                          ? `0 20px 45px rgba(0,0,0,0.5), 0 0 25px ${feature.bg}`
                          : `0 22px 45px rgba(15, 23, 42, 0.1), 0 0 20px ${feature.bg}`,
                        '& .card-icon-orb': {
                          transform: 'scale(1.1) rotate(6deg)',
                        },
                        '& .card-explore-btn': {
                          bgcolor: feature.color,
                          color: '#ffffff',
                          borderColor: feature.color,
                          boxShadow: `0 6px 18px ${feature.bg}`,
                          '& .arrow-icon': {
                            transform: 'translateX(4px)',
                          },
                        },
                      },
                    }}
                  >
                    {/* Visual Card Header Banner */}
                    <Box
                      sx={{
                        position: 'relative',
                        height: 140,
                        width: '100%',
                        background: feature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Geometric background mesh pattern */}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          opacity: 0.18,
                          backgroundImage: `radial-gradient(circle at 20% 30%, #ffffff 1px, transparent 1px), radial-gradient(circle at 80% 70%, #ffffff 1.5px, transparent 1.5px)`,
                          backgroundSize: '18px 18px',
                        }}
                      />

                      {/* Number Pill Top-Left */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 14,
                          left: 14,
                          background: 'rgba(0, 0, 0, 0.35)',
                          backdropFilter: 'blur(8px)',
                          color: '#ffffff',
                          borderRadius: '12px',
                          px: 1.4,
                          py: 0.4,
                          fontSize: '0.75rem',
                          fontWeight: 800,
                          letterSpacing: '0.05em',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          zIndex: 2,
                        }}
                      >
                        {feature.id.toString().padStart(2, '0')}
                      </Box>

                      {/* Category Badge Top-Right */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 14,
                          right: 14,
                          background: 'rgba(255, 255, 255, 0.22)',
                          backdropFilter: 'blur(8px)',
                          color: '#ffffff',
                          borderRadius: '12px',
                          px: 1.4,
                          py: 0.4,
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          zIndex: 2,
                        }}
                      >
                        {feature.badge}
                      </Box>

                      {/* 3D Glass Icon Orb in Center */}
                      <Box
                        className="card-icon-orb"
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '20px',
                          background: 'rgba(255, 255, 255, 0.25)',
                          backdropFilter: 'blur(12px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.4)',
                          border: '1.5px solid rgba(255, 255, 255, 0.4)',
                          transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                          zIndex: 2,
                        }}
                      >
                        {feature.icon}
                      </Box>
                    </Box>

                    {/* Card Content */}
                    <CardContent
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        textAlign: 'left',
                      }}
                    >
                      {/* Title */}
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{
                          fontSize: '1.08rem',
                          color: isDarkMode ? '#ffffff' : '#0f172a',
                          mb: 1.2,
                          lineHeight: 1.35,
                        }}
                      >
                        {feature.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.84rem',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          mb: 2.5,
                          lineHeight: 1.65,
                          flexGrow: 1,
                        }}
                      >
                        {feature.description}
                      </Typography>

                      {/* Micro Highlights Tags */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 3 }}>
                        {feature.highlights.map((tag, tIdx) => (
                          <Box
                            key={tIdx}
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              py: 0.35,
                              px: 1.1,
                              borderRadius: '8px',
                              bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                              color: isDarkMode ? '#cbd5e1' : '#475569',
                              border: isDarkMode
                                ? '1px solid rgba(255, 255, 255, 0.06)'
                                : '1px solid #e2e8f0',
                            }}
                          >
                            {tag}
                          </Box>
                        ))}
                      </Box>

                      {/* Action Button */}
                      <Button
                        className="card-explore-btn"
                        variant="outlined"
                        onClick={() => handleOpen(globalIndex)}
                        endIcon={
                          <ArrowForwardRoundedIcon
                            className="arrow-icon"
                            sx={{ fontSize: '0.95rem', transition: 'transform 0.25s ease' }}
                          />
                        }
                        sx={{
                          textTransform: 'none',
                          py: 1,
                          px: 2.2,
                          fontSize: '0.84rem',
                          fontWeight: 700,
                          borderRadius: '16px',
                          border: isDarkMode
                            ? '1.5px solid rgba(255, 255, 255, 0.12)'
                            : '1.5px solid #e2e8f0',
                          color: isDarkMode ? '#ffffff' : '#1e293b',
                          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : '#ffffff',
                          transition: 'all 0.3s ease',
                          width: '100%',
                        }}
                      >
                        Explore Feature
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </Box>
      </Box>

      {/* Comprehensive Feature Modal */}
      <Modal
        open={openIndex !== null}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 300,
          sx: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(5, 10, 20, 0.8)' },
        }}
      >
        <Fade in={openIndex !== null}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: isDarkMode ? '#0d1527' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#0f172a',
              borderRadius: '28px',
              boxShadow: isDarkMode
                ? '0 25px 60px rgba(0,0,0,0.7), 0 0 40px rgba(7, 164, 245, 0.15)'
                : '0 25px 60px rgba(15,23,42,0.18)',
              border: isDarkMode
                ? '1px solid rgba(255, 255, 255, 0.12)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              p: { xs: 3, md: 5 },
              maxWidth: 780,
              width: '92%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            {openIndex !== null && features[openIndex] && (
              <Box>
                {/* Modal Top Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '16px',
                        background: features[openIndex].gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        boxShadow: `0 8px 20px ${features[openIndex].bg}`,
                      }}
                    >
                      {features[openIndex].icon}
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={features[openIndex].badge}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.68rem',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            bgcolor: features[openIndex].bg,
                            color: features[openIndex].color,
                            border: `1px solid ${features[openIndex].border}`,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}
                        >
                          Step {features[openIndex].id.toString().padStart(2, '0')}
                        </Typography>
                      </Box>
                      <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                        {features[openIndex].title}
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    onClick={handleClose}
                    sx={{
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                      '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : '#e2e8f0' },
                    }}
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                {/* Video Demo if available */}
                {features[openIndex].video && (
                  <Box sx={{ mb: 3, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 12px 30px rgba(0,0,0,0.3)' }}>
                    <video
                      src={features[openIndex].video}
                      controls
                      autoPlay
                      muted
                      style={{
                        width: '100%',
                        maxHeight: '360px',
                        display: 'block',
                        backgroundColor: '#000',
                      }}
                    />
                  </Box>
                )}

                {/* Full Details Content */}
                <Typography
                  variant="body1"
                  sx={{
                    color: isDarkMode ? '#cbd5e1' : '#334155',
                    lineHeight: 1.8,
                    fontSize: '0.96rem',
                    mb: 3.5,
                  }}
                >
                  {features[openIndex].fullDetails}
                </Typography>

                {/* Key Deliverables / Capabilities List */}
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: '18px',
                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                    mb: 4,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    sx={{ color: features[openIndex].color, mb: 1.5, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.78rem' }}
                  >
                    Key Platform Capabilities
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                    {features[openIndex].highlights.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <CheckCircleRoundedIcon sx={{ color: features[openIndex].color, fontSize: 18 }} />
                        <Typography variant="body2" fontWeight={600} sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Direct Store CTAs in Modal */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    pt: 2,
                    borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #f1f5f9',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                      component="a"
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<GooglePlayIcon size={18} />}
                      sx={{
                        background: '#07a4f5',
                        color: '#ffffff',
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        borderRadius: '20px',
                        px: 2.4,
                        py: 0.9,
                        textTransform: 'none',
                        boxShadow: '0 6px 18px rgba(7, 164, 245, 0.35)',
                        '&:hover': {
                          background: '#068bc2',
                        },
                      }}
                    >
                      Get on Google Play
                    </Button>
                    <Button
                      component="a"
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<AppleIcon size={18} />}
                      sx={{
                        background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#0f172a',
                        color: '#ffffff',
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        borderRadius: '20px',
                        px: 2.4,
                        py: 0.9,
                        textTransform: 'none',
                        '&:hover': {
                          background: isDarkMode ? 'rgba(255,255,255,0.15)' : '#1e293b',
                        },
                      }}
                    >
                      App Store (iOS)
                    </Button>
                  </Box>

                  <Button
                    onClick={handleClose}
                    variant="text"
                    sx={{
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default FeaturesGridSection;
